import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import { AppDispatch, RootState } from 'redux/store';
import Header from 'components/Common/Header';
import Tab from 'components/Common/Tab';
import TextInput from 'components/Common/TextInput';
import routes from 'constants/routes';
import Button from 'components/Common/Button';
import AnnouncementContainer from 'components/Announcemnet/AnnouncementContainer';
import AnnouncementGroupContainer from 'components/Announcemnet/AnnouncementGroupContainer';
import { fetchAnnouncements } from 'redux/slices/announcements/announcementsSlice';
import MuiLoader from 'components/Common/Loader';
import { fetchAnnouncementGroups } from 'redux/slices/announcements/announcementGroupSlice';
import { ANNOUNCEMENT_GROUPS, ANNOUNCEMENTS, AnnouncementsTabs } from 'types/announcements';
import { setAnnouncementsTab } from 'redux/slices/common/appSlice';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});
const Announcements: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { currentTab, tableState, announcementList, announcementGroupsList } = useSelector((state: RootState) => ({
        currentTab: state.app.announcement?.currentTab,
        tableState: state.app.table,
        announcementList: state.announcement.announcementsList,
        announcementGroupsList: state.announcement.announcementGroups,
    }));

    const { data: announcements, loading: announcementListLoading } = announcementList || {};
    const { records: announcementGroups, loading: announcementGroupLoading } = announcementGroupsList || {};

    const getAnnouncementsData = useCallback(() => {
        switch (currentTab) {
            case ANNOUNCEMENTS:
                return dispatch(fetchAnnouncements());
            case ANNOUNCEMENT_GROUPS:
                return dispatch(fetchAnnouncementGroups());
            default:
                return null;
        }
    }, [currentTab, dispatch, tableState]); // eslint-disable-line

    useEffect(() => {
        // REMOVE THE CONDITION WHEN ANNOUNCEMENT_GROUPS API SUPPORT THE PAGINATION.
        // IT WILL ALSO SHOW LOADING WHEN USER WILL NAVIGATE FROM 1 TO 0 PAGE AS PAGE NUMBER WILL BE FALSE.
        if (currentTab === ANNOUNCEMENT_GROUPS && tableState?.pageNumber) return;
        getAnnouncementsData();
    }, [currentTab, getAnnouncementsData, tableState]);

    const getLoading = useMemo(() => {
        switch (currentTab) {
            case ANNOUNCEMENT_GROUPS:
                return announcementGroupLoading;
            default:
                return announcementListLoading;
        }
    }, [currentTab, announcementGroupLoading, announcementListLoading]);

    const ButtonNavigationHandler = useCallback(() => {
        const targetRoute = currentTab === ANNOUNCEMENTS ? routes.announcements.new : routes.announcements.createGroup;
        navigate(targetRoute);
    }, [currentTab, navigate]);

    // Switch Between Different Containers Based On Tab Value.
    const content = useMemo(() => {
        switch (currentTab) {
            case ANNOUNCEMENTS:
                return <AnnouncementContainer data={announcements} loading={getLoading} />;
            case ANNOUNCEMENT_GROUPS:
                return <AnnouncementGroupContainer data={announcementGroups} loading={getLoading} />;
            default:
                return null;
        }
    }, [currentTab, announcements, announcementGroups, tableState, getLoading]); //eslint-disable-line

    const handleSetTab = useCallback(
        (tab: AnnouncementsTabs) => {
            dispatch(setAnnouncementsTab(tab));
        },
        [dispatch]
    );

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader absolute loading={getLoading} />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 22,
                            flexDirection: 'row',
                            justifyContent: 'space-around',
                        }}
                    >
                        <Tab
                            tabs={[
                                { value: ANNOUNCEMENTS, label: 'Announcements' },
                                { value: ANNOUNCEMENT_GROUPS, label: 'Announcement Groups' },
                            ]}
                            tabValue={currentTab}
                            setTabValue={handleSetTab}
                        />
                    </div>
                    <Button
                        onClick={ButtonNavigationHandler}
                        sx={{ m: 2, width: { xs: '50%', md: '25%' }, fontSize: { xs: 14, lg: 18 } }}
                        title={currentTab === ANNOUNCEMENTS ? 'Create Announcement' : 'Create new Group'}
                    />
                    {/* <div style={{ padding: 16 }}>
                        <TextInput />
                    </div> */}
                    <div>{content}</div>
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Announcements;
